/*
 * SectionNavigation Specification
 * @module components
 * @flow
 */

import React from 'react';
import styled, { css } from 'styled-components/macro';
import { ifProp } from 'styled-tools';
import * as R from 'ramda';

import { SectionNavigationTab } from './components';
import { Block } from '~/components/Block';

export type SectionNavigationProps = $Shape<DeviceQuery> & {
  navRef: React$ElementRef<*>,
  routes: Object,
  scrollRef: React$ElementRef<*>,
  tabs: {
    link: string,
    route: string,
    ref: React$ElementRef<*>,
  },
};

const SectionNavigationScroll = styled(Block)`
  position: relative;
  z-index: 2;
  border-radius: 0;
  overflow-x: auto;
  max-width: 100%;

  ${ifProp(
    { device: 'desktop' },
    css`
      display: flex;
      justify-content: center;
    `,
  )}
`;

const SectionNavigationWrapper = styled.div.attrs(() => ({
  role: 'tablist',
}))`
  display: flex;
  width: 100%;
  white-space: nowrap;

  ${ifProp(
    { device: 'desktop' },
    css`
      padding: 0 18px;
      justify-content: center;
    `,
    css`
      padding: 0 14px;
    `,
  )}
`;

/**
 * SectionNavigation React-component
 * @memberof components
 * @extends Block
 * @param {Object} props - properties
 * @param {$PropertyType<DeviceQuery, 'device'>} [props.device] - device mode
 * @param {React$ElementRef<*>} [props.navRef] - navigation element reference
 * @param {Object} [props.routes] - routes list
 * @param {React$ElementRef<*>} [props.scrollRef] - scroll element reference
 * @param {Object} [props.tabs] - tabs configs
 * @example <caption>Simple usage</caption>
 * <SectionNavigation />
 * @returns {React.Node<*>} React-component
 */
export const SectionNavigation = ({
  device,
  navRef,
  routes,
  scrollRef,
  tabs,
  settings,
}: SectionNavigationProps) => {
  const filteredTabs = R.pipe(
    R.values,
    R.filter(({ condition }) => R.isNil(condition) || condition),
    // $FlowIssue
    R.addIndex(R.map)(({ icon, label, link }, i) => (
      <SectionNavigationTab
        locale={settings && settings.locale}
        icon={icon}
        key={i}
        label={label}
        link={link}
        tabs={tabs[i].tabs}
        tabRef={tabs[i].ref}
      />
    )),
  )(routes);

  return (
    filteredTabs.length && (
      <SectionNavigationScroll device={device} ref={scrollRef}>
        <SectionNavigationWrapper
          device={device}
          ref={navRef}
          testid="navigation"
        >
          {filteredTabs}
        </SectionNavigationWrapper>
      </SectionNavigationScroll>
    )
  );
};

SectionNavigation.defaultProps = {
  tabs: [],
};
